import { Fragment, useState } from 'react'
import { useTranslation } from 'next-i18next'

import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import {
  Session,
  Tutor,
  ProductCategory,
  SessionStatus as SessionStatusType,
  SessionStudent,
} from '~/types/tutoring-api'

import SessionExpandCollapseIcon from './components/SessionExpandCollapseIcon'
import SessionIcon from './components/SessionIcon'
import SessionInfoLine from './components/SessionInfoLine'
import SessionMessage from './components/SessionMessage'
import SessionParticipantInfo from './components/SessionParticipantInfo'
import SessionStatus from './components/SessionStatus'
import SessionTimeInfo from './components/SessionTimeInfo'
import SessionType from './components/SessionType'
import { getMeeting } from './Session.utils'

export default function SessionItem({
  session,
  forceExpanded = false,
  isTutor = false,
  sessionTutor,
  sessionStudent,
  setSessionStatus,
  withWrapper = true,
}: {
  session: Session
  forceExpanded?: boolean
  isTutor?: boolean
  sessionTutor?: Tutor
  sessionStudent?: SessionStudent
  setSessionStatus?: (sessionId: number, status: SessionStatusType) => void
  withWrapper?: boolean
}): React.ReactElement {
  const { t } = useTranslation(['generics', 'sessionStatus'])
  const [isExpanded, setIsExpanded] = useState(false)
  const expanded = forceExpanded || isExpanded

  const fullName = isTutor ? sessionStudent?.fullName : sessionTutor?.fullName
  const email = isTutor ? sessionStudent?.email : sessionTutor?.email
  const { link, linkType } = getMeeting(session)

  if (!session) {
    return <Fragment />
  }

  const handleOnCardClick = (): void => {
    if (!forceExpanded && isExpanded === false) {
      setIsExpanded(true)
    }
  }

  return (
    <div
      aria-label={expanded ? undefined : 'Open session'}
      className={`relative ${withWrapper ? 'p-3 rounded shadow-card bg-white' : ''}`}
      onClick={handleOnCardClick}
      role={expanded ? undefined : 'button'}
      tabIndex={expanded ? undefined : 0}
    >
      <SessionExpandCollapseIcon
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(false)}
        shouldHide={forceExpanded}
      />
      <SessionIcon isExpanded={expanded} type={session.type} />
      {!expanded && <SessionType type={session.type} />}
      {!expanded && <span className="block text-xxs font-semibold">{fullName}</span>}
      <div className={`space-y-3 flex flex-col ${expanded ? 'text-xs' : 'text-xxs'}`}>
        <SessionTimeInfo endTime={session.endTime} startTime={session.startTime} />
        {expanded && (
          <Fragment>
            <SessionParticipantInfo email={email} name={fullName} />
            <SessionMessage message={session.properties?.needHelpWith} />
            <div className="space-y-1.5 max-w-xs">
              <SessionInfoLine text={link} type="link" />
              <SessionInfoLine text={session.location} type="location" />
            </div>
            <SessionStatus
              displaySetSessionStatus={isTutor}
              sessionId={session.id}
              setSessionStatus={setSessionStatus}
              status={session.status}
            />
          </Fragment>
        )}
        {link && expanded && (
          <span className="pb-2 space-y-2 flex flex-col items-center">
            <ButtonPrimary href={link} link={linkType} size="x-small" stopPropagation>
              {session.type === ProductCategory.TUTORING ? t('generics:openSession') : t('generics:videoChat')}
            </ButtonPrimary>
          </span>
        )}
      </div>
    </div>
  )
}
